/**
 * A reusable component abstraction of a core react-table table instance.
 * The passed columns and data should be memoized values with the useMemo hook.
 */

import { useContext, useEffect } from "react";
import {
    useTable, usePagination,
    TableOptions, TableState, PluginHook, Column
} from "react-table";

import { REACT_APP_PAGINATION_ELEMENTS } from "../../../../../../shared/constants";
// context import
import { AppContext } from "../../../../../../core/contexts/AppContext";

// SCSS import
import "./RevisioniReactTable.scss";

// type relativo alla creazione della table
type ReactTableProps = {
    columns: Column[],
    data: any,
    displaysMissingDataError?: boolean,
    tableHooks?: PluginHook<{}> | ((hooks: any) => void),
    border?:any
} & ({
    initialState: Partial<TableState> | undefined
} | {
    initialState?: Partial<TableState>
})


/**
 * @param {props} props the props of the component
 * @param {Column[]} props.columns MEMOIZED columns of the table
 * @param {any} props.data MEMOIZED data of the table
 * @param {Partial<TableState> | undefined} props.initialState the initial state of the table
 * @param {PluginHook<{}> | ((hooks: any) => void)} props.tableHooks the table hooks
 * @returns {JSX.Element}  the react-table component
 */
const RevisioniReactTable = (props: ReactTableProps) => {
    const { appText } = useContext(AppContext);
    const { COMPONENTS_TEXT } = appText;

    const getPresentTableHooks = () => {
        
        const outputHooks: PluginHook<{}>[] = [];
        outputHooks.push(usePagination);
        props?.tableHooks && outputHooks.push(props.tableHooks);

        return outputHooks;
    };

    // Imposta le option della table
    const getTableOptions = () => {
        const outputOptions: TableOptions<{}> = { columns: props.columns, data: props.data };
        if (props.initialState) {
            outputOptions.initialState = props.initialState;
        }
        return outputOptions;
    };

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex}
    } = useTable(
        {
            ...getTableOptions()
        },
        ...getPresentTableHooks()
    );

    // All'avvio dell'app setta il numero di row per pagina  
    useEffect(()=>{
        setPageSize(parseInt(REACT_APP_PAGINATION_ELEMENTS));
    }, [setPageSize]);

    return (
        <>
            <table className="w-100 Table"
                {...getTableProps()}>
                <thead className="TableHead">
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => (
                                <th className={`TableHead__cell${props.border ? "ReactTable__border" : ""}`} {...column.getHeaderProps()}>{column.render("Header")}</th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody className="TableBody" {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row);
                        return (
                            <tr className="TableBody__row" {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return (
                                        <td className={`TableBody__cell ${props.border ? "ReactTable__border" : ""}`} {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                    );
                                })}
                            </tr>
                        );
                    })}

                </tbody>
            </table>

            { (props.data === undefined || JSON.stringify(props.data) === "[]") && props.displaysMissingDataError &&
            <div className="ReactTable__table-emptyBlock">
                <p className="ReactTable__table-emptyBlock-p">
                    {COMPONENTS_TEXT.TABLE_MISSING_DATA}
                </p>
            </div>
            }

            <div className="TablePagination">
                <div className="TablePagination__wrapper">
                    <button
                        className="TablePagination__PageNav bi bi-chevron-left"
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}>
                    </button>
                    <div className="TablePagination__PageIndices">
                        {canPreviousPage ?
                            <button 
                                className="TablePagination__PageIndex" 
                                onClick={() => previousPage()}
                            >
                                {pageIndex}
                            </button>
                            : null
                        }
                        <button className="TablePagination__PageIndex --current">
                            {pageIndex + 1}
                        </button>
                        {canNextPage ?
                            <button 
                                className="TablePagination__PageIndex" 
                                onClick={() => nextPage()}
                            >
                                {pageIndex + 2}
                            </button>
                            : null
                        }
                    </div>
                    <button
                        className="TablePagination__PageNav bi bi-chevron-right"
                        onClick={() => nextPage()}
                        disabled={!canNextPage}>
                    </button>
                </div>
            </div>
        </>
    );
};

export default RevisioniReactTable;
